/**
 * Chat Header Component
 *
 * Header for the chat panel with title and action buttons.
 */

import React from 'react';
import { Box, Typography, IconButton, Tooltip, Chip, Menu, MenuItem, ListItemIcon, ListItemText, Button } from '@mui/material';
import AiChatIcon from 'features/ui/app-layout/rail/ai-chat-icon';
import CloseIcon from '@mui/icons-material/Close';
import MinimizeIcon from '@mui/icons-material/Minimize';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import useAIChatStore from '../../../stores/ai-chat-store';

interface ChatHeaderProps {
  onClose?: () => void;
  onClearHistory?: () => void;
  onRestartConversation?: () => void;
  onNewChat?: () => void;
  showMinimize?: boolean;
  showFullscreen?: boolean;
  showClose?: boolean;
}

const ChatHeader: React.FC<ChatHeaderProps> = ({
  onClose,
  onClearHistory,
  onRestartConversation,
  onNewChat,
  showMinimize = true,
  showFullscreen = true,
  showClose = true,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const { isFullscreen, toggleFullscreen, minimize, wizard } = useAIChatStore((state) => ({
    isFullscreen: state.isFullscreen,
    toggleFullscreen: state.toggleFullscreen,
    minimize: state.minimize,
    wizard: state.wizard,
  }));

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleClearHistory = () => {
    handleMenuClose();
    onClearHistory?.();
  };

  const handleNewChat = () => {
    handleMenuClose();
    if (onNewChat) {
      onNewChat();
    } else if (onRestartConversation) {
      onRestartConversation();
    } else {
      onClearHistory?.();
    }
  };

  return (
    <Box className={classes.header}>
      <Box className={classes.titleSection}>
        <Box className={classes.logoWrapper}>
          <AiChatIcon className={classes.logo} />
          <Box className={classes.onlineIndicator} />
        </Box>
        <Box className={classes.titleText}>
          <Typography variant="subtitle1" className={classes.title}>
            {t('paiAssistant', 'SAIA')}
          </Typography>
          {wizard.active ? (
            <Chip
              label={`${t('step', 'Step')} ${wizard.currentStep}/${wizard.totalSteps}`}
              size="small"
              color="primary"
              variant="outlined"
              className={classes.wizardChip}
            />
          ) : (
            <Typography variant="caption" className={classes.subtitle}>
              {t('onlineReadyToHelp', 'Online • Ready to help')}
            </Typography>
          )}
        </Box>
      </Box>

      <Box className={classes.actions}>
        {/* New Chat Button */}
        <Tooltip title={t('newChat', 'New chat')}>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={handleNewChat}
            className={classes.newChatButton}
          >
            {t('new', 'New')}
          </Button>
        </Tooltip>

        <Tooltip title={t('moreOptions', 'More options')}>
          <IconButton size="small" onClick={handleMenuOpen} className={classes.actionButton}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {showFullscreen && (
          <Tooltip title={isFullscreen ? t('exitFullscreen', 'Exit fullscreen') : t('fullscreen', 'Fullscreen')}>
            <IconButton size="small" onClick={toggleFullscreen} className={classes.actionButton}>
              {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}

        {showMinimize && (
          <Tooltip title={t('minimize', 'Minimize')}>
            <IconButton size="small" onClick={minimize} className={classes.actionButton}>
              <MinimizeIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {showClose && onClose && (
          <Tooltip title={t('close', 'Close')}>
            <IconButton size="small" onClick={onClose} className={classes.actionButton}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleNewChat}>
          <ListItemIcon>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('newConversation', 'New conversation')} />
        </MenuItem>
        <MenuItem onClick={handleClearHistory}>
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('clearHistory', 'Clear history')} />
        </MenuItem>
      </Menu>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.greys.lighter}`,
    backgroundColor: `${theme.palette.background.paper}E6`,
    backdropFilter: 'blur(10px)',
    minHeight: 56,
    position: 'sticky',
    top: 0,
    zIndex: 5,
  },
  titleSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  logoWrapper: {
    position: 'relative',
    width: 40,
    height: 40,
    borderRadius: '50%',
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: `0 4px 16px ${theme.palette.primary.main}4D`,
  },
  logo: {
    color: theme.palette.primary.contrastText,
    fontSize: '1.5rem',
  },
  onlineIndicator: {
    position: 'absolute',
    bottom: 2,
    right: 2,
    width: 10,
    height: 10,
    borderRadius: '50%',
    backgroundColor: theme.palette.success.main,
    border: `2px solid ${theme.palette.background.paper}`,
  },
  titleText: {
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    fontWeight: 600,
    lineHeight: 1.2,
    color: theme.palette.text.primary,
  },
  subtitle: {
    color: theme.palette.text.secondary,
    fontSize: '0.7rem',
  },
  wizardChip: {
    height: 20,
    fontSize: '0.65rem',
    marginTop: 2,
    borderColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  actionButton: {
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.greys.lightest,
    },
  },
  newChatButton: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.8rem',
    padding: theme.spacing(0.5, 1.5),
    minWidth: 'auto',
    borderRadius: 10,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    boxShadow: `0 8px 20px ${theme.palette.primary.main}33`,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}));

export default ChatHeader;

